import { z } from "zod";
import { idempotencyKeySchema } from "./common.js";
import { tierSchema } from "./tier.js";

/**
 * Phase 13h — `billing.*` procedures. Wires the Lite → Home upgrade
 * flow per `MVP.md` §4.9.
 *
 * The user taps an Upgrade-to-unlock CTA (Reports hub from 13g, the
 * tier-cap banner from 13b/13c). The client calls
 * `billing.createCheckoutSession`; the server brokers the Stripe
 * Checkout session and returns a `url` the client redirects to.
 *
 * After the Stripe transaction completes, Stripe POSTs a webhook event
 * to `billing.webhook`. The handler updates the user's tier (Clerk
 * publicMetadata in production; an in-memory `TierUpgradeStore` in
 * tests) and the client re-fetches its tier on its next opportunity
 * (the `/billing/return` route invalidates the relevant TanStack Query
 * caches, the Clerk session reload picks up the new tier).
 *
 * Stripe wiring is **behind the `StripeCheckoutService` seam** in the
 * API and the `PaymentsService` seam in the web client so the Phase
 * 13.5 native wrap can swap to StoreKit / Play Billing without
 * touching consumer code (mirroring the camera + geolocation seams
 * 13e/13f established).
 *
 * Schema design notes:
 *
 * - `billingCreateCheckoutSessionInputSchema` carries the **target
 *   tier** (`home` for the MVP upgrade) and an idempotency key so a
 *   double-tap on the Upgrade button returns the same Checkout URL
 *   (matches the Phase 12d idempotency-by-key pattern; the per-tier
 *   product / price id lookup is server-side).
 * - The output is just `{ url, sessionId }` — the client redirects via
 *   `window.location.assign(url)`. The session id surfaces so the
 *   `/billing/return` landing can correlate against the webhook
 *   outcome if needed.
 * - `billingWebhookEventSchema` is the minimal Stripe-event envelope
 *   the handler trusts: `id` + `type` + `data.object` (the Stripe
 *   Checkout session object). Signature verification happens upstream
 *   of the schema check (in the `StripeCheckoutService`'s
 *   `verifyWebhookSignature` method); the schema is a defense-in-depth
 *   check on the post-verify payload shape.
 */

export const billingCreateCheckoutSessionInputSchema = z.object({
  idempotencyKey: idempotencyKeySchema,
  /** Target tier for the upgrade. MVP only accepts `"home"`. */
  targetTier: tierSchema,
  /**
   * Where Stripe redirects after success / cancel. The client passes
   * its absolute return URLs (the `/billing/return` route +
   * `/billing/canceled`). Kept thin — the server doesn't compose URLs
   * for the client because the client's deployment URL can differ
   * (custom domain vs Cloudflare Pages preview).
   */
  successUrl: z.string().url(),
  cancelUrl: z.string().url(),
});
export type BillingCreateCheckoutSessionInput = z.infer<
  typeof billingCreateCheckoutSessionInputSchema
>;

export const billingCreateCheckoutSessionOutputSchema = z.object({
  url: z.string().url(),
  sessionId: z.string().min(1),
});
export type BillingCreateCheckoutSessionOutput = z.infer<
  typeof billingCreateCheckoutSessionOutputSchema
>;

/**
 * Phase 13h — Stripe webhook event envelope. Verbatim of the subset
 * the handler reads. Stripe-event types out of scope for MVP collapse
 * to a passthrough success.
 *
 * The `data.object.client_reference_id` carries the Clerk `userId`
 * (the API stamps it on the Checkout session at creation); the handler
 * trusts this field after Stripe-signature verification.
 *
 * `data.object.metadata.targetTier` mirrors the Checkout request — the
 * webhook applies it to the user's profile so the next session
 * request picks up the new tier.
 */
export const billingWebhookEventSchema = z.object({
  id: z.string().min(1),
  type: z.string().min(1),
  data: z.object({
    object: z.object({
      id: z.string().min(1),
      client_reference_id: z.string().min(1).nullable().optional(),
      metadata: z
        .object({
          targetTier: tierSchema.optional(),
        })
        .nullable()
        .optional(),
      status: z.string().optional(),
    }),
  }),
});
export type BillingWebhookEvent = z.infer<typeof billingWebhookEventSchema>;

export const billingWebhookOutputSchema = z.object({
  received: z.literal(true),
  applied: z.boolean(),
});
export type BillingWebhookOutput = z.infer<typeof billingWebhookOutputSchema>;
